<template>
  <div>
    <v-row class="my-4" v-if="!loading && totalRecords > 0">
      <v-col cols="12" class="d-flex justify-end align-end">
        <ColumnsSelector
          persistenceKey="teamActivity__findingsTab"
          :headers="headers"
          :limitActiveColumns="columnsLimit"
          @toggleColumn="onToggleColumn"
          @close="trackColumnsSelector"
          @setInitialColumns="setInitialColumns"
        />
      </v-col>
    </v-row>

    <v-data-table
      v-show="!loading && totalRecords > 0"
      data-private
      calculate-widths
      :headers="columns"
      :footer-props="footerOptions"
      :items="rows"
      :options.sync="tableOptions"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :server-items-length="totalRecords"
      :loading="loading"
      :mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
      :no-data-text="$t('home.teams_activity.findings_tab_empty')"
      @click:row="onFindingDrawerToggle(true, $event.id)"
    >
      <template v-slot:[`item.name`]="{ item }">
        <div class="d-flex">
          <v-tooltip top max-width="300px">
            <template v-slot:activator="{ on }">
              <span v-on="on" v-snip="1" class="body-2 link-td">{{
                item.name
              }}</span>
            </template>
            {{ item.name }}
          </v-tooltip>
          <div class="ml-1" v-if="item.isOverdue">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small color="red"
                  >mdi-alert-circle-outline</v-icon
                >
              </template>
              {{ $t('home.to_do.overdue') }}
            </v-tooltip>
          </div>
        </div>
      </template>

      <template v-slot:[`item.dueDate`]="{ item }">
        <span
          v-if="item.dueDate"
          class="body-2 blue-grey--text text--darken-3"
          >{{ item.dueDate }}</span
        >

        <div v-else class="text-start">-</div>
      </template>

      <template v-slot:[`item.assignees`]="{ item }">
        <ChipAvatar small :account="item.assignees[0]" />

        <span
          v-if="item.assignees.length > 1"
          class="caption blue-grey--text text--darken-3"
        >
          +{{ item.assignees.length - 1 }}
        </span>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <FindingStatus :status="item.status" />
      </template>

      <template v-slot:[`item.priority`]="{ item }">
        <PriorityChip v-if="item.priority" :priority="item.priority" />

        <div v-else class="text-start">-</div>
      </template>

      <template v-slot:[`item.findingCreatedDate`]="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">
          {{ item.findingCreatedDate }}
        </span>
      </template>

      <template v-slot:[`item.updatedAt`]="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">
          {{ item.updatedAt }}
        </span>
      </template>

      <template v-slot:[`item.categories`]="{ item }">
        <ChipList outlined :items="item.categories" />
      </template>

      <template v-slot:[`item.situationType`]="{ item }">
        <ChipList outlined :items="item.situationType" />
      </template>

      <template v-slot:[`item.addressedDate`]="{ item }">
        <span
          v-if="item.addressedDate"
          class="body-2 blue-grey--text text--darken-3"
          >{{ item.addressedDate }}</span
        >

        <div v-else class="text-start">-</div>
      </template>

      <template v-slot:[`item.routineExecution`]="{ item }">
        <v-tooltip top max-width="300px">
          <template v-slot:activator="{ on }">
            <span
              v-on="on"
              v-snip="1"
              class="body-2 blue-grey--text text--darken-3"
            >
              {{ item.routineExecution }}</span
            >
          </template>
          {{ item.routineExecution }}
        </v-tooltip>
      </template>

      <template v-slot:[`item.routineAssignee`]="{ item }">
        <ChipAvatar small :account="item.routineAssignee" />
      </template>

      <template v-slot:item.tags="{ item }">
        <ChipList v-if="item.tags.length > 0" outlined :items="item.tags" />
        <div v-else class="text-start">-</div>
      </template>

      <template v-slot:[`item.createdBy`]="{ item }">
        <ChipAvatar small :account="item.createdBy" />
      </template>
    </v-data-table>

    <TeamActivityEmptyState v-if="!loading && totalRecords === 0" />

    <TableSkeleton class="pt-12" v-if="loading" :columns="columns.length" />

    <FindingDrawer
      v-if="isOpenFindingDrawer"
      :visible="isOpenFindingDrawer"
      :findingId="selectedFindingId"
      @close="onFindingDrawerToggle(false)"
      @deleted="onFindingDelete"
      @edited="fetchFindings(tableOptions)"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import api from '@/api'
import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import ChipAvatar from '@/components/ChipAvatar.vue'
import PriorityChip from '@/components/PriorityChip/PriorityChip.vue'
import ChipList from '../ChipList.vue'
import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'
import TeamActivityEmptyState from './TeamActivityEmptyState.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import TableSkeleton from '@/components/TableSkeleton.vue'
import FindingStatus from '../FindingDrawer/FindingStatus.vue'

const INITIAL_ITEMS_PER_PAGE = 20

export default {
  name: 'FindingsTab',

  props: {
    teamId: {
      type: String,
      required: false,
    },

    columnsLimit: {
      type: Number,
      required: false,
      default: null,
    },
  },

  mixins: [mixin, trackingMixin],

  components: {
    ChipAvatar,
    PriorityChip,
    ChipList,
    FindingDrawer,
    TeamActivityEmptyState,
    TableSkeleton,
    ColumnsSelector,
    FindingStatus,
  },

  data() {
    return {
      findings: [],
      loading: false,
      isOpenFindingDrawer: false,
      selectedFindingId: '',
      totalRecords: 0,
      sortBy: 'updatedAt',
      sortDesc: true,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('common.name'),
          value: 'name',
          show: true,
          width: 234,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.due_date'),
          value: 'dueDate',
          show: true,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('common.assignee_one'),
          value: 'assignees',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('common.priority'),
          value: 'priority',
          show: false,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('filters.date.creation_date'),
          value: 'findingCreatedDate',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('filters.date.modified_date'),
          value: 'updatedAt',
          show: true,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('common.category_other'),
          value: 'categories',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('organization_settings.situation_type'),
          value: 'situationType',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('home.teams_activity.addressed_date'),
          value: 'addressedDate',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('routines.routine_execution'),
          value: 'routineExecution',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('home.teams_activity.routine_assignee'),
          value: 'routineAssignee',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.tag_other'),
          value: 'tags',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('home.teams_activity.created_by'),
          value: 'createdBy',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
      ],
    }
  },

  methods: {
    async fetchFindings(options) {
      try {
        this.loading = true
        const { data } = await api.findings.getAll({
          organizationId: this.organizationId,
          relations: ['assignees.photo', 'routine.createdBy.photo', 'tags'],
          filters: {
            teams: [this.teamId],
          },
          sort: {
            by: options.sortBy ?? 'updatedAt',
            order: options.sortDesc ? 'desc' : 'asc',
          },
          limit: options.itemsPerPage,
          skip: options.itemsPerPage * (options.page - 1),
        })
        this.findings = data.results ?? []
        this.totalRecords = data.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    onFindingDrawerToggle(value, findingId = '') {
      if (value) {
        this.trackHomeNavigate({
          origin: 'teamActivity',
          destination: 'finding',
        })
      }

      this.isOpenFindingDrawer = value
      this.selectedFindingId = findingId
    },

    onFindingDelete() {
      this.onFindingDrawerToggle(false)
      this.fetchFindings(this.tableOptions)
    },

    getParsedCategories(categories) {
      return (
        categories?.map(category => this.$t(`common.categories.${category}`)) ??
        []
      )
    },

    getParsedTags(tags) {
      return tags?.map?.(tag => tag.name) ?? []
    },

    getParsedSituationTypes(situationTypes) {
      return (
        situationTypes?.map(situation =>
          this.$t(
            `quick_action.situation_types.situations.${situation.situation}`
          )
        ) ?? []
      )
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
    },

    trackColumnsSelector() {
      this.trackTeamNavigate({
        section: 'findingsTab',
        element: 'columnsSelector',
        selectedColumns: this.headers
          .filter(col => col.show === true)
          .map(col => col.text),
      })
    },

    setInitialColumns(selectedColumns) {
      this.headers = this.getInitialSelectedTableColumns(
        this.headers,
        selectedColumns
      )
    },
  },

  computed: {
    ...mapGetters(['organizationId']),

    rows() {
      return this.findings.map(finding => {
        return {
          id: finding.id,
          name: finding.situation,
          dueDate: finding.dueDate
            ? utils.dates.formatDate(finding.dueDate, 'MMM D, YYYY, h:mm A')
            : '',
          isOverdue: finding.dueDate
            ? moment().isAfter(finding.dueDate) &&
              finding.status !== 'completed'
            : false,
          assignees: finding.assignees,
          status: finding.status,
          priority: finding.priority,
          findingCreatedDate: finding.createdAt
            ? utils.dates.formatDate(finding.createdAt, 'MMM D, YYYY, h:mm A')
            : '',
          updatedAt: finding.updatedAt
            ? utils.dates.formatDate(finding.updatedAt, 'MMM D, YYYY, h:mm A')
            : '',
          categories: this.getParsedCategories(finding.categories) ?? [],
          tags: this.getParsedTags(finding.tags),
          situationType:
            this.getParsedSituationTypes(finding.situationTypes) ?? [],
          addressedDate: finding.addressedAt
            ? utils.dates.formatDate(finding.addressedAt, 'MMM D, YYYY, h:mm A')
            : '',
          routineExecution: finding.routine?.name,
          routineAssignee: finding.routine?.createdBy,
          createdBy: finding.routine?.createdBy,
        }
      })
    },

    columns() {
      return this.headers.filter(h => h.show)
    },

    tableOptions: {
      get() {
        return {
          itemsLength: this.totalRecords,
          itemsPerPage: INITIAL_ITEMS_PER_PAGE,
          page: 1,
          multiSort: false,
        }
      },

      set(event) {
        this.fetchFindings({
          itemsLength: this.totalRecords,
          itemsPerPage: event.itemsPerPage,
          page: event.page,
          sortDesc: event.sortDesc[0] ?? true,
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  watch: {
    teamId() {
      this.fetchFindings(this.tableOptions)
    },
  },
}
</script>
